import { useEffect, useState } from 'react'
import { Button, Group, Modal, Stack, Text } from '@mantine/core'

interface SessionTimeoutModalProps {
  /** Epoch millis of the moment the server signs the user out. */
  deadline: number
  onStaySignedIn: () => void
  onSignOut: () => void
}

/**
 * The inactivity warning (docs/plans/jwt-cookie-auth.md, D4). `AuthProvider` mounts it two minutes
 * before the server stops honouring a token refresh and unmounts it when the question is settled;
 * "Stay signed in" is a refresh — the one kind of user action that counts as activity — and
 * dismissing the dialog (Escape) means the same thing.
 */
export function SessionTimeoutModal({ deadline, onStaySignedIn, onSignOut }: SessionTimeoutModalProps) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [])

  const remainingMs = Math.max(0, deadline - now)

  return (
    <Modal
      opened
      onClose={onStaySignedIn}
      title="Still there?"
      centered
      withCloseButton={false}
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text>
          You have been inactive for a while. To protect your account you will be signed out in{' '}
          <strong data-testid="session-timeout-remaining">{formatRemaining(remainingMs)}</strong>.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onSignOut}>
            Sign out
          </Button>
          <Button onClick={onStaySignedIn} data-autofocus>
            Stay signed in
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

/** `m:ss`, rounded up so the dialog never shows 0:00 while the user can still act. */
function formatRemaining(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
